import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as R from 'ramda';

const CarriersAccordion = ({ carriers, defaultExpanded, isCarrierRestricted }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const [isExpanded, setExpanded] = useState(defaultExpanded);

  const showCount = isCarrierRestricted === 'false' || carriers.length > 1;
  const countLabel = isCarrierRestricted === 'false' ? 'ALL' : carriers.length;
  const countStyles = isCarrierRestricted === 'false' ? { minWidth: 38 } : {};

  return (
    <Accordion
      expanded={R.isEmpty(carriers) ? false : isExpanded}
      onChange={() => setExpanded(!isExpanded)}
      sx={{ m: 0 }}
    >
      <AccordionSummary
        aria-controls="customer-info-content"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
      >
        <Stack direction="row" spacing={1}>
          <Typography variant="body2">
            {!R.isEmpty(carriers) ? 'Carriers' : 'All carriers allowed to load'}
          </Typography>
          {showCount && (
            <Chip label={countLabel} sx={countStyles} type="accordion" variant="badge" />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        {isCarrierRestricted === 'false' ? (
          <Typography variant="subtitle1">All Carriers Allowed</Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                lg: '2fr 1fr 1fr',
                xs: '1fr 1fr',
              },
              rowGap: 1,
            }}
          >
            <Typography
              sx={{
                gridColumn: {
                  lg: '1/4',
                  xs: '1/3',
                },
              }}
              variant="subtitle1"
            >
              Allowed Carriers
            </Typography>
            <Typography variant="caption">Carrier Name</Typography>
            {lgUp ? (
              <>
                <Typography variant="caption">Carrier FEIN</Typography>
                <Typography variant="caption">City, State</Typography>
              </>
            ) : (
              <Typography variant="caption">FEIN / City, State</Typography>
            )}

            {carriers.map(({ carrierId, carrierName, carrierFEIN, carrierCity, carrierState }) => (
              <Fragment key={carrierId}>
                <Typography variant="subtitle2">{carrierName}</Typography>
                {lgUp ? (
                  <>
                    <Typography variant="subtitle2">{carrierFEIN}</Typography>
                    <Typography variant="subtitle2">{`${carrierCity}, ${carrierState}`}</Typography>
                  </>
                ) : (
                  <Box>
                    <Typography variant="subtitle2">{carrierFEIN}</Typography>
                    <Typography variant="subtitle2">{`${carrierCity}, ${carrierState}`}</Typography>
                  </Box>
                )}
              </Fragment>
            ))}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

CarriersAccordion.propTypes = {
  carriers: PropTypes.arrayOf(PropTypes.shape({})),
  defaultExpanded: PropTypes.bool,
  isCarrierRestricted: PropTypes.oneOf(['false', 'true']).isRequired,
};

CarriersAccordion.defaultProps = {
  carriers: [],
  defaultExpanded: false,
};

export default CarriersAccordion;
